import { createContext, useContext, useState, useCallback } from "react";

const CallStateContext = createContext(null);

const PENDING_WRAPUP_KEY = "chairside_pending_wrapup";

export const CallStateProvider = ({ children }) => {
  const [activeCallLead, setActiveCallLead] = useState(null);
  const [activeTwilioCallSid, setActiveTwilioCallSid] = useState(null);
  const [voicemailUrl, setVoicemailUrl] = useState(null);
  const [lastCallStatus, setLastCallStatus] = useState(null);
  const [isDialerActive, setIsDialerActive] = useState(false);
  const [showWrapUp, setShowWrapUp] = useState(false);
  // Tracks who started the call: 'lead-queue' | 'manual-dialer' | 'front-desk' | 'recare' | null
  const [callSource, setCallSource] = useState(null);

  // Power dialer session stored here so it survives page navigation
  const [dialerSession, setDialerSession] = useState(null);

  // Pending wrapup that must be completed (survives refresh via localStorage)
  const [pendingWrapUp, setPendingWrapUp] = useState(() => {
    try {
      const stored = localStorage.getItem(PENDING_WRAPUP_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.leadId && Date.now() - parsed.savedAt < 3600000) {
          return parsed;
        }
        localStorage.removeItem(PENDING_WRAPUP_KEY);
      }
    } catch {}
    return null;
  });

  const savePendingWrapUp = useCallback((data) => {
    setPendingWrapUp(data);
    if (data) {
      try { localStorage.setItem(PENDING_WRAPUP_KEY, JSON.stringify({ ...data, savedAt: Date.now() })); } catch {}
    } else {
      try { localStorage.removeItem(PENDING_WRAPUP_KEY); } catch {}
    }
  }, []);

  const clearCallState = useCallback(() => {
    setActiveCallLead(null);
    setActiveTwilioCallSid(null);
    setLastCallStatus(null);
    setShowWrapUp(false);
    setCallSource(null);
  }, []);

  return (
    <CallStateContext.Provider
      value={{
        activeCallLead,
        setActiveCallLead,
        activeTwilioCallSid,
        setActiveTwilioCallSid,
        voicemailUrl,
        setVoicemailUrl,
        lastCallStatus,
        setLastCallStatus,
        isDialerActive,
        setIsDialerActive,
        showWrapUp,
        setShowWrapUp,
        callSource,
        setCallSource,
        dialerSession,
        setDialerSession,
        pendingWrapUp,
        savePendingWrapUp,
        clearCallState,
      }}
    >
      {children}
    </CallStateContext.Provider>
  );
};

export const useCallState = () => {
  const ctx = useContext(CallStateContext);
  if (!ctx) {
    throw new Error("useCallState must be used within a CallStateProvider");
  }
  return ctx;
};

export default CallStateContext;
